import { forwardRef, useImperativeHandle, useState } from "react";
import { useIntl } from "react-intl";
import {
  autoUpdate,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { AgeCategory } from "@shared/graphql";
import { actions } from "../../../../i18n/sharable-defs";
import { BookingInputHandler, BookingInputProps } from "./index";
import Button from "../../../common/button";
import GuestsResumeInput from "../../../inputs/readonly/guests";
import GuestsSelector from "../../../inputs/guests";
import "./index.scss";

type Props = BookingInputProps<Record<AgeCategory, number>> & {
  withIcon?: boolean;
  minGuests?: Partial<Record<AgeCategory, number>>;
  maxGuests?: number;
}

const GuestsInput = forwardRef<BookingInputHandler, Props>(
  ({ sticked, withIcon = false, minGuests, maxGuests, value, onChange = () => void 0 }, forwardedRef) => {

    const intl = useIntl();
    const [ pickerOpen, setPickerOpen ] = useState<boolean>(false);

    const { refs, context, floatingStyles } = useFloating({
      open: pickerOpen,
      onOpenChange: setPickerOpen,
      strategy: "absolute",
      placement: "bottom-end",
      whileElementsMounted: autoUpdate,
      middleware: [
        size({
          apply: ({ rects, elements }) => {
            elements.floating.style.minWidth = `${rects.reference.width}px`;
          }
        }),
        offset(({ rects, elements }) => {
          elements.floating.style.setProperty("--ccv-article-aside-reference-width", `${rects.reference.width}px`);
          return -1 * rects.reference.height;
        })
      ]
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
      useClick(context, { toggle: false }),
      useDismiss(context, { outsidePress: true })
    ]);

    useImperativeHandle(forwardedRef, () => ({
      open: () => setPickerOpen(true)
    }));

    if (sticked)
      return (<GuestsSelector min={minGuests} max={maxGuests} value={value} onChange={onChange} />);

    return (
      <>
        <GuestsResumeInput ref={refs.setReference}
                           {...getReferenceProps()}
                           withIcon={withIcon}
                           value={value}/>
        {pickerOpen &&
          <FloatingPortal>
            <div ref={refs.setFloating}
                 {...getFloatingProps()}
                 className="vars-for-portal picker-wrap guests"
                 style={floatingStyles}>
              <div className="picker-header">
                <GuestsResumeInput className="faking-ref" withIcon={withIcon} value={value} />
              </div>
              <GuestsSelector min={minGuests} max={maxGuests} value={value} onChange={onChange} />
              <div className="picker-footer">
                <Button onClick={() => setPickerOpen(false)}>
                  {intl.formatMessage(actions.close)}
                </Button>
              </div>
            </div>
          </FloatingPortal>
        }
      </>)

});

export default GuestsInput;
